<template>
  <div>
    <add-or-update-member-profile-modal
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />
    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :show-search="true"
      :show-filter-by-default="true"
      :show-import-button="can('csv_upload_data:CanImport')"
      import-type="bio_data"
      @import="onImport"
      @row-clicked="onView"
    >
      <template #filters>
        <SearchFilter @search="fetchAllData" />
      </template>

      <template #cell(member)="data">
        <b-media vertical-align="center">
          <template #aside>
            <b-avatar
              size="32"
              :src="getValueFromSource(data, 'item.avatar.path')"
              :text="sentenceCase(avatarText(`${data.item.full_name}`))"
              :variant="`light-primary`"
              :to="viewRoute(data.item)"
            />
          </template>
          <b-link
            :to="viewRoute(data.item)"
            class="font-weight-bold d-block text-nowrap"
          >
            {{ sentenceCase(getValueFromSource(data, 'item.full_name')) }}
          </b-link>
          <span class="text-muted">{{ data.item.email }}</span><br />
        </b-media>
      </template>

      <template #cell(region)="data">
        {{ getGeoData(data.item, 'region') }}
      </template>

      <template #cell(area)="data">
        {{ getGeoData(data.item, 'area') }}
      </template>

      <template #cell(district)="data">
        {{ getGeoData(data.item, 'district') }}
      </template>

      <template #cell(assembly)="data">
        {{ getGeoData(data.item, 'assembly') }}
      </template>

      <template v-if="can('csv_upload_data:CanList')" #action_button>
        <b-button :to="{ name: 'import-reports' }" variant="primary" class="d-flex align-items-center" style="gap: 5px;">
          <feather-icon icon="ClipboardIcon" />
          View Import Report
        </b-button>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item :to="viewRoute(data.item)">
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50">View</span>
          </b-dropdown-item>
          <b-dropdown-item :to="editRoute(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="ministersOnly ? can('ministers:CanDelete') : can('member_profiles:CanDelete')" @click="() => onDeleteProfile(data.item)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>
  
<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import SearchFilter from '@/@core/components/query-builder/search-filter.vue';
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateMemberProfileModal from "@/@core/components/shared/church/AddOrUpdateMemberProfileModal.vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    SearchFilter,
    BDropdownItem,
    AddOrUpdateMemberProfileModal
  },
  directives: {
    Ripple,
  },
  props: {
    ministersOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      records: [],

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'member', label: 'Member', sortable: false, },
        { key: 'member_type.title', label: 'Member Type', sortable: false, },
        { key: 'member_title.title', label: 'Title', sortable: false, },
        { key: 'member_designation.designation', label: 'Designation', sortable: false, },
        { key: 'region', label: "Region", sortable: false, },
        { key: 'area', label: "Area", sortable: false, formatter: val => val || '-' },
        { key: 'district', label: "District", sortable: false, formatter: val => val || '-' },
        { key: 'assembly', label: "Assembly", sortable: false, formatter: val => val || '-' },
        { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){ 
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search,
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
  },
  methods: {
    async fetchAllData(filters = {}) {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        }

        if (typeof this.ministersOnly === "boolean") {
          query.ministers_only = this.ministersOnly;
        }

        if (filters && filters.filter_mode && filters.filters) {
          query.filters = JSON.stringify(filters);
        }

        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.memberProfiles.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    viewRoute(data) {
      if (!data || !data._id) return {};
      const name = this.ministersOnly ? "ministers" : "members"
      const route = { name: `${name}-view`, params: { id: data._id } }
      return route
    },
    editRoute(data) {
      if (!data || !data._id) return {};
      const name = this.ministersOnly ? "ministers" : "members"
      const route = { name: `${name}-detail`, params: { id: data._id } }
      return route
    },
    onView(data) {
      this.$router.push(this.viewRoute(data));
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null
      }
    },
    async onImport(file) {
      try {
        this.loading = true;
        const formData = new FormData();
        formData.append("ministers_bio", file);

        const response = await this.useJwt().mainService.users.importBioData(formData);
        this.importReport = this.getValueFromSource(response, 'data.data');

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: "User records import has been processed successfully."
          },
        });
        this.$router.push({
          name: 'import-reports-detail',
          params: { id: this.importReport.id }
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          }
        });
      } finally {
        this.loading = false;
      }
    },
    async onDeleteProfile(profile) {
      try {
        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this profile and related user data?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.value) return;

        this.loading = true;
        await this.useJwt().mainService.memberProfiles.delete(profile._id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Deleted!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Profile and related user data deleted successfully',
          },
        });
        this.fetchAllData();
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>
  

  
